import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { RootState } from "../../app/store";
import { fetchAvailableTimeSlots } from "../sessions/slice-extensions/sessions-slice-extension";
import { AvailableTimeSlot } from "../sessions/time-slot/models/AvailableTimeSlot";
import { CalendarView } from "./models/CalendarView";
import { parseYmd, toYmd } from "./utils/calendar-range";

/** The slot service computes forward from today; asking beyond this is pointless. */
const MAX_HORIZON_DAYS = 60;

const daysFromTodayTo = (ymd: string): number => {
  const target = parseYmd(ymd);
  const today = parseYmd(toYmd(new Date()));
  return Math.ceil((target.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)) + 1;
};

/**
 * Free time to show inside the week grid. The slot service only computes forward
 * from today, so a past or far-future week legitimately has none — the caller
 * simply renders nothing. Month cells are too small for slots, so month view
 * doesn't fetch at all.
 */
export const useCalendarFreeSlots = (view: CalendarView, to: string): AvailableTimeSlot[] => {
  const dispatch = useAppDispatch();
  const slots = useAppSelector((state: RootState) => state.sessions.timeSlots.availableTimeSlots);
  const days = daysFromTodayTo(to);
  const withinHorizon = view === "week" && days > 0 && days <= MAX_HORIZON_DAYS;

  useEffect(() => {
    if (withinHorizon) {
      dispatch(fetchAvailableTimeSlots({ days }));
    }
  }, [dispatch, withinHorizon, days]);

  return withinHorizon ? slots : [];
};
